import gatewayService from '../../../services/GatewayService';
import * as CONSTANTS from '../../constants';
import tokenStorageService from '../../../services/TokenStorageService';
import DateStorageService from '../../../services/DateStorageService';
import {eventTracker} from "../../../services/GoogleAnalyticsService";

/**
 * Download booking csv file.
 *
 * @param commit
 * @param payload
 */
export const downloadFile = ({commit}, payload) => {
    commit(CONSTANTS.CSV, payload)
};

/**
 * Get bookings form period.
 *
 * @param context
 * @param payload
 */
export const getBookings = ({ commit, state }, payload) => {
    commit(CONSTANTS.BOOKING_FETCH_PENDING);
    const token = tokenStorageService.get();
    return gatewayService.getBookings(
        state.bookings.filters.bookingPeriod.from,
        state.bookings.filters.bookingPeriod.to,
        state.bookings.filters.arrivalPeriod.from,
        state.bookings.filters.arrivalPeriod.to,
        payload,
        token
    ).then((res) => {
        if (res.status === 200) {
            commit(CONSTANTS.BOOKING_FETCH_SUCCESS, res.data.data);
        } else {
            commit(CONSTANTS.BOOKING_FETCH_NO_CONTENT);
        }
    }).catch(() => {
        commit(CONSTANTS.BOOKING_FETCH_FAILURE, {
            level: 'error',
            text: 'booking.messages.error'
        })
    });
};

/**
 * Get bookings form period.
 *
 * @param context
 * @param payload
 */
export const getSupplierFilterData = ({ commit }, payload) => {
    commit(CONSTANTS.BOOKING_FILTER_PENDING);
    const token = tokenStorageService.get();
    return gatewayService.getSupplierFilterData(payload, token).then((res) => {
        commit(CONSTANTS.BOOKING_FILTER_SUCCESS, res.data.data);
    }).catch(() => {
        commit(CONSTANTS.BOOKING_FETCH_FAILURE, {
            level: 'error',
            text: 'booking.messages.error'
        });
    });
};

/**
 * Updates the selected filters
 *
 * @param context
 * @param rootState
 * @param payload
 */
export const setSelectedFilters = ({ state, commit, rootState }, payload) => {
    const user = rootState.auth.user;

    if (payload.hasOwnProperty('brand')) {
        payload.domain = undefined;
    } else if (payload.hasOwnProperty('site')) {
        payload.accommodation = undefined;
    }
    commit(CONSTANTS.BOOKING_SET_SELECTED_FILTERS, {
        data: payload,
        // Google Analytics
        meta: eventTracker('booking', 'bookings', 'bookings filtered by user', user.id)
    });
    commit(CONSTANTS.BOOKING_PAGE_SET, 1);

    if (payload.hasOwnProperty('supplier')) {
        getBookings({commit, state});
    }
};

/**
 * Sets the current page
 *
 * @param context
 * @param payload
 */
export const setBookingPagination = ({commit}, payload) => {
    commit(CONSTANTS.BOOKING_PAGE_SET, payload);
};

/**
 * Increments the current page
 *
 * @param context
 */
export const incrementBookingPagination = ({commit}) => {
    commit(CONSTANTS.BOOKING_PAGE_INCREMENT);
};

/**
 * Decrements the current page
 *
 * @param context
 */
export const decrementBookingPagination = ({commit}) => {
    commit(CONSTANTS.BOOKING_PAGE_DECREMENT);
};

/**
 * Updates the predicate to order by
 *
 * @param context
 * @param payload
 */
export const setPredicate = ({commit}, payload) => {
    commit(CONSTANTS.BOOKING_PREDICATE_SET, payload);
    commit(CONSTANTS.BOOKING_PAGE_SET, 1);
};

/**
 * Updates the limit per page
 *
 * @param context
 * @param rootState
 * @param payload
 */
export const setLimit = ({ commit, rootState }, payload) => {
    const user = rootState.auth.user;

    commit(CONSTANTS.BOOKING_LIMIT_SET, {
        data: payload,
        // Google Analytics
        meta: eventTracker('booking', 'bookings', 'user changed the view limit', user.id)
    });
    commit(CONSTANTS.BOOKING_PAGE_SET, 1);
};

/**
 * Updates the limit per page
 *
 * @param context
 * @param rootState
 * @param payload
 */
export const setOrder = ({ commit, rootState }, payload) => {
    const user = rootState.auth.user;

    commit(CONSTANTS.BOOKING_ORDER_SET, {
        data: payload,
        // Google Analytics
        meta: eventTracker('booking', 'bookings', 'limit changed by user', user.id)
    });
    commit(CONSTANTS.BOOKING_PAGE_SET, 1);
};

/**
 * Updates the current booking period
 *
 * @param context
 * @param rootState
 * @param payload
 */
export const setBookingPeriod = ({ commit, rootState }, payload) => {
    const user = rootState.auth.user;

    commit(CONSTANTS.BOOKING_SET_BOOKING_PERIOD, {
        data: payload,
        // Google Analytics
        meta: eventTracker('booking', 'bookings', 'booking period changed', user.id)
    });

    DateStorageService.setBookingBookingPeriodInStorage(payload);
};

/**
 * Updates the current arrival period
 *
 * @param context
 * @param payload
 */
export const setArrivalPeriod = ({ commit, rootState }, payload) => {
    const user = rootState.auth.user;

    commit(CONSTANTS.BOOKING_SET_ARRIVAL_PERIOD, {
        data: payload,
        // Google Analytics
        meta: eventTracker('booking', 'bookings', 'arrival period changed', user.id)
    });

    DateStorageService.setBookingArrivalPeriodInStorage(payload);
};

/**
 * Set the predicate for the multi select dropdown
 *
 * @param commit
 * @param payload
 */
export const setMultiPredicate = ({commit}, payload) => {
    commit(CONSTANTS.BOOKING_MULTI_PREDICATE_SET, payload);
};

/**
 * Unset the predicate for the multi select dropdown
 *
 * @param commit
 * @param payload
 */
export const unsetMultiPredicate = ({commit}, payload) => {
    commit(CONSTANTS.BOOKING_MULTI_PREDICATE_UNSET, payload);
};

/**
 * Reset the multi select dropdown
 *
 * @param commit
 */
export const resetMultiPredicate = ({commit}) => {
    commit(CONSTANTS.BOOKING_MULTI_PREDICATE_RESET);
};

/**
 * Select a specific predicate for the multi dropdown
 *
 * @param commit
 * @param payload
 */
export const selectMultiPredicate = ({commit}, payload) => {
    commit(CONSTANTS.BOOKING_MULTI_PREDICATE_SELECT, payload);
};

/**
 * Update the search query.
 *
 * @param commit
 * @param rootState
 * @param payload
 */
export const search = ({ commit, rootState }, payload) => {
    const user = rootState.auth.user;

    commit(CONSTANTS.BOOKING_FILTER_SEARCH, {
        data: payload, // Google Analytics
        meta: eventTracker('booking', 'bookings', 'user searched for booking', user.id)
    });
    commit(CONSTANTS.BOOKING_PAGE_SET, 1);
};

/**
 * Reset the bookings state.
 *
 * @param commit
 */
export const resetState = ({ commit }) => {
    commit(CONSTANTS.BOOKING_RESET_STATE);
};



// WEBPACK FOOTER //
// ./src/store/modules/booking/actions.js